import { createContext, useContext, useMemo, type ReactNode } from 'react';
import { useSelector } from 'react-redux';
import { selectSession, selectFeatureFlags } from '@client/model/selectors';
import { SessionType } from '@shared';
import {
  getCompletionStepIds,
  getFlatStepConfigs,
  getGuidedPlanningVariant,
  type FlatStepConfig,
} from '@/features/guided-financial-planning/config';
import { useGuidedPlanningSession } from '@/features/guided-financial-planning/hooks/use-guided-planning-session';

type SessionApi = ReturnType<typeof useGuidedPlanningSession>;

export type GuidedPlanningContextType = SessionApi & {
  flatStepConfigs: FlatStepConfig[];
};

const GuidedPlanningContext = createContext<GuidedPlanningContextType | null>(null);

export function useGuidedPlanningContext() {
  const context = useContext(GuidedPlanningContext);
  if (!context) {
    throw new Error('useGuidedPlanningContext must be used within GuidedPlanningProvider');
  }
  return context;
}

/** Active wizard steps (respects `guidedPlanningPremiumBudgetStep`). */
export function useGuidedPlanningFlatStepConfigs(): FlatStepConfig[] {
  return useGuidedPlanningContext().flatStepConfigs;
}

/** Read-only access to the current session */
export function useSession() {
  const { session, isLoading, isError, error } = useGuidedPlanningContext();
  return { session, isLoading, isError, error };
}

/** Access to session mutation functions */
export function useSessionMutations() {
  const {
    updateStep,
    updateStepData,
    completeStepWithData,
    completeSession,
    abandonSession,
    isSaving,
  } = useGuidedPlanningContext();
  return {
    updateStep,
    updateStepData,
    completeStepWithData,
    completeSession,
    abandonSession,
    isSaving,
  };
}

/** Check if the session is complete (read-only) */
export function useIsSessionComplete() {
  const { session } = useGuidedPlanningContext();
  return session?.status === 'completed';
}

interface GuidedPlanningProviderProps {
  clientId: string;
  children: ReactNode;
}

export function GuidedPlanningProvider({ clientId, children }: GuidedPlanningProviderProps) {
  const { sessionType } = useSelector(selectSession);
  const reduxFeatureFlags = useSelector(selectFeatureFlags);
  const isD2C = sessionType === SessionType.client && !!reduxFeatureFlags.d2cMode;

  const variant = getGuidedPlanningVariant(reduxFeatureFlags);
  const financialFeasibility = !!reduxFeatureFlags.financialFeasibility;
  const genworthMode = !!reduxFeatureFlags.genworthMode;
  const flatStepConfigs = useMemo(
    () => getFlatStepConfigs(variant, financialFeasibility, genworthMode),
    [variant, financialFeasibility, genworthMode],
  );
  const completionStepIds = useMemo(
    () => getCompletionStepIds(flatStepConfigs),
    [flatStepConfigs],
  );

  const sessionApi = useGuidedPlanningSession(clientId, completionStepIds);

  const value = useMemo(
    () => ({
      ...sessionApi,
      flatStepConfigs,
    }),
    [sessionApi, flatStepConfigs],
  );

  return <GuidedPlanningContext.Provider value={value}>{children}</GuidedPlanningContext.Provider>;
}
